import { useState, useEffect, useRef } from 'react';
import userService from '../services/userService';
import parkingService from '../services/parkingService';
import adminService from '../services/adminServices';
import vehicleService from '../services/vehicleService';
import { Edit, Car, X, Upload } from 'lucide-react';
import AdminParkingManagement from '../components/AdminParkingManagement';
import '../styles/usuariosStyle.css';

const Gestion_de_usuarios = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [filterCedula, setFilterCedula] = useState('');
  const [registeredParkings, setRegisteredParkings] = useState([]);
  const [parkingsLoading, setParkingsLoading] = useState(false);
  const [selectedParkingIds, setSelectedParkingIds] = useState([]); // CAMBIADO: ahora es array para selección múltiple
  const [adminData, setAdminData] = useState(null); // AGREGADO: para almacenar datos del admin al editar

  const [showAdminParkingModal, setShowAdminParkingModal] = useState(false);

  // Estado para vehículo
  const [vehicleData, setVehicleData] = useState({
    plate: '',
    brand: '',
    model: '',
    type: 'Carro'
  });
  const [userVehicles, setUserVehicles] = useState([]);
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [editingVehiclePlate, setEditingVehiclePlate] = useState(null); // placa original del vehículo que se está editando

  // Estado para carga masiva
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const bulkFileRef = useRef(null);

  // Paginacion
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 20;

  // Estado para formulario
  const [formData, setFormData] = useState({
    name: '',
    lastname: '',
    email: '',
    idCard: '',
    typeID: '',
    password: '',
    role: '',
    isEmailVerified: false,
    subscription: ''
  });

  // Estado para usuarios
  const [users, setUsers] = useState([]);
  // Estado para usuarios filtrados
  const [filteredUsers, setFilteredUsers] = useState([]);

  // Función para cargar usuarios
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getAllUsers();
      setUsers(data);
      setFilteredUsers(data);
      setError(null);
    } catch (err) {
      setError('Error al cargar los usuarios. Por favor, intente de nuevo más tarde.');
      console.error('Error al cargar usuarios:', err);
    } finally {
      setLoading(false);
    }
  };

  // MODIFICADO: Función mejorada para cargar parqueos usando adminService
  const loadParkings = async () => {
    try {
      setParkingsLoading(true);
      const response = await adminService.getAvailableParkings();
      if (response.success && Array.isArray(response.parkings)) {
        setRegisteredParkings(response.parkings);
      } else {
        setRegisteredParkings([]);
      }

    } catch (error) {
      console.error('❌ Error al cargar parqueos:', error);

      let errorMessage = 'Error desconocido';
      if (!error.response) {
        errorMessage = 'No se puede conectar al servidor. Verifica que el backend esté corriendo.';
      } else if (error.response.status === 404) {
        errorMessage = 'Endpoint de parqueos no encontrado.';
      } else if (error.response.status === 500) {
        errorMessage = `Error del servidor: ${error.response.data?.message || 'Error interno'}`;
      } else {
        errorMessage = error.response.data?.message || error.message;
      }

      console.error('📝 Error específico:', errorMessage);
      setError(`Error al cargar parqueos: ${errorMessage}`);
      setRegisteredParkings([]);
    } finally {
      setParkingsLoading(false);
    }
  };

  // AGREGADO: Función para obtener datos del administrador (incluidos parqueos asignados)
  const getAdminData = async (adminIdCard) => {
    try {
      const response = await adminService.getAllAdmins();
      if (response.success) {
        const admin = response.admins.find(a => a.idCard === adminIdCard);
        return admin || null;
      }
      return null;
    } catch (error) {
      console.error('Error al obtener datos del administrador:', error);
      return null;
    }
  };

  // Cargar usuarios al montar el componente
  useEffect(() => {
    fetchUsers();
    loadParkings();
  }, []);

  // MODIFICADO: Limpiar selección de parqueos cuando cambia el rol
  useEffect(() => {
    if (formData.role !== 'Admin') {
      setSelectedParkingIds([]);
      setAdminData(null);
    }
  }, [formData.role]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleVehicleChange = (e) => {
    const { name, value } = e.target;
    setVehicleData(prev => ({ ...prev, [name]: value }));
  };

  const handleDeleteVehicle = async (plate) => {
    if (!window.confirm(`¿Eliminar el vehículo con placa ${plate}?`)) return;
    try {
      await vehicleService.deleteVehicle(formData.idCard, plate);
      setUserVehicles(prev => prev.filter(v => (v.plate || v) !== plate));
    } catch (err) {
      console.error('Error al eliminar vehículo:', err);
      setError('Error al eliminar el vehículo: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleEditVehicle = (vehicle) => {
    const plate = vehicle.plate || vehicle;
    setEditingVehiclePlate(plate);
    setVehicleData({
      plate: plate,
      brand: vehicle.brand || '',
      model: vehicle.model || '',
      type: vehicle.type || 'Carro'
    });
    setShowVehicleForm(true);
  };

  const handleCancelVehicleEdit = () => {
    setEditingVehiclePlate(null);
    setVehicleData({ plate: '', brand: '', model: '', type: 'Carro' });
    setShowVehicleForm(false);
  };

  const handleSaveVehicle = async () => {
    if (!vehicleData.plate || !vehicleData.brand || !vehicleData.model) {
      setError('Completa todos los campos del vehículo (placa, marca, modelo)');
      return;
    }
    try {
      if (editingVehiclePlate) {
        // Actualizar vehículo existente
        await vehicleService.updateVehicle(editingVehiclePlate, {
          plate: vehicleData.plate.toUpperCase(),
          brand: vehicleData.brand,
          model: vehicleData.model,
          type: vehicleData.type
        });
      } else {
        // Agregar nuevo vehículo
        await vehicleService.addVehicle({
          idCard: formData.idCard,
          plate: vehicleData.plate.toUpperCase(),
          brand: vehicleData.brand,
          model: vehicleData.model,
          type: vehicleData.type
        });
      }
      // Recargar la lista de vehículos
      const vehiclesResponse = await vehicleService.getUserVehicles(formData.idCard);
      setUserVehicles(vehiclesResponse.vehicles || vehiclesResponse || []);
      setVehicleData({ plate: '', brand: '', model: '', type: 'Carro' });
      setEditingVehiclePlate(null);
      setShowVehicleForm(false);
      setError('');
    } catch (err) {
      const vehError = err.response?.data?.message || err.message;
      setError(`Error al ${editingVehiclePlate ? 'actualizar' : 'agregar'} vehículo: ` + vehError);
    }
  };

  const handleFilterChange = (e) => {
    setFilterCedula(e.target.value);
  };

  const applyFilter = () => {
    setCurrentPage(1);
    if (!filterCedula.trim()) {
      setFilteredUsers(users);
      return;
    }

    const filtered = users.filter(user =>
      user.idCard.toLowerCase().includes(filterCedula.toLowerCase())
    );
    setFilteredUsers(filtered);
  };

  const resetFilter = () => {
    setFilterCedula('');
    setFilteredUsers(users);
    setCurrentPage(1);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // MODIFICADO: Función mejorada para editar que carga parqueos asignados
  const handleEdit = async (user) => {
    setFormData({
      _id: user._id,
      name: user.name,
      lastname: user.lastname,
      email: user.email,
      idCard: user.idCard,
      typeID: user.typeID,
      password: '',
      role: user.role,
      isEmailVerified: user.isEmailVerified || false,
    });

    // Si es un administrador, cargar sus parqueos asignados
    if (user.role === 'Admin') {
      const adminInfo = await getAdminData(user.idCard);
      if (adminInfo) {
        setAdminData(adminInfo);
        const assignedParkingIds = adminInfo.assignedParkings?.map(p => p._id) || [];
        setSelectedParkingIds(assignedParkingIds);
      }
    } else {
      setAdminData(null);
      setSelectedParkingIds([]);
    }

    // Cargar vehículos del usuario
    try {
      const vehiclesResponse = await vehicleService.getUserVehicles(user.idCard);
      const vehicles = vehiclesResponse.vehicles || vehiclesResponse || [];
      setUserVehicles(vehicles);
    } catch (err) {
      console.error('Error al cargar vehículos del usuario:', err);
      setUserVehicles([]);
    }

    // Resetear formulario de vehículo
    setVehicleData({ plate: '', brand: '', model: '', type: 'Carro' });
    setEditingVehiclePlate(null);
    setShowVehicleForm(false);

    setEditMode(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (userId) => {
    if (window.confirm('¿Está seguro que desea eliminar este usuario?')) {
      try {
        await userService.deleteUser(userId);
        const updatedUsers = users.filter(user => user.idCard !== userId);
        setUsers(updatedUsers);
        setFilteredUsers(updatedUsers);
      } catch (err) {
        setError('Error al eliminar el usuario');
        console.error(err);
      }
    }
  };

  const handleOpenAdminParkingModal = () => {
    setShowAdminParkingModal(true);
  };

  const handleCloseAdminParkingModal = () => {
    setShowAdminParkingModal(false);
  };

  const handleRefreshAfterParkingUpdate = () => {
    fetchUsers();
  };

  // AGREGADO: Función para manejar selección múltiple de parqueos
  const handleParkingSelection = (parkingId) => {
    setSelectedParkingIds(prev => {
      if (prev.includes(parkingId)) {
        return prev.filter(id => id !== parkingId);
      } else {
        return [...prev, parkingId];
      }
    });
  };

  // MODIFICADO: Función mejorada para manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      // Basic validation
      if (!formData.email || !formData.name || !formData.idCard) {
        setError('Los campos email, nombre y cédula son obligatorios');
        return;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('El formato del email no es válido');
        return;
      }

      // Password validation for new users
      if (!editMode && (!formData.password || formData.password.length < 6)) {
        setError('La contraseña debe tener al menos 6 caracteres');
        return;
      }

      const userData = {
        idCard: formData.idCard,
        email: formData.email,
        name: formData.name,
        lastname: formData.lastname,
        role: formData.role || 'UserApp',
        typeID: formData.typeID || 'Cédula',
        isEmailVerified: formData.isEmailVerified || false
      };

      // Agregar password solo si es necesario
      if (!editMode || (formData.password && formData.password.trim() !== '')) {
        userData.password = formData.password;
      }

      console.log('Submitting user data:', userData);
      console.log('Selected parking IDs:', selectedParkingIds);

      if (editMode) {
        const userId = formData.idCard;
        await userService.updateUser(userId, userData);

        // Si es administrador, actualizar asignación de parqueos
        if (formData.role === 'Admin' && adminData) {
          try {
            await adminService.assignParkingsToAdmin(adminData.idCard, selectedParkingIds);
          } catch (parkingErr) {
            console.error('Error al asignar parqueos (no crítico):', parkingErr);
          }
        }
      } else {
        // Crear nuevo usuario
        if (formData.role === 'Admin') {
          await adminService.createAdminWithParkings(userData, selectedParkingIds);
        } else {
          await userService.createUser(userData);
        }
      }

      // Registrar vehículo si se llenaron los datos
      let vehicleMessage = '';
      if (showVehicleForm && vehicleData.plate && vehicleData.brand && vehicleData.model) {
        try {
          await vehicleService.addVehicle({
            idCard: formData.idCard,
            plate: vehicleData.plate.toUpperCase(),
            brand: vehicleData.brand,
            model: vehicleData.model,
            type: vehicleData.type
          });
          vehicleMessage = ' y vehículo registrado';
        } catch (vehicleErr) {
          const vehError = vehicleErr.response?.data?.message || vehicleErr.message;
          console.error('Error al registrar vehículo:', vehicleErr);
          vehicleMessage = `. Error al registrar vehículo: ${vehError}`;
        }
      }

      // Reload the user list
      fetchUsers();

      // Reset the form
      setFormData({
        name: '',
        lastname: '',
        email: '',
        idCard: '',
        typeID: 'Cédula',
        password: '',
        role: 'UserApp',
        isEmailVerified: false,
        subscription: ''
      });

      setSelectedParkingIds([]);
      setAdminData(null);
      setVehicleData({ plate: '', brand: '', model: '', type: 'Carro' });
      setEditingVehiclePlate(null);
      setUserVehicles([]);
      setShowVehicleForm(false);
      setEditMode(false);
      setError('');

      alert((editMode ? 'Usuario actualizado con éxito' : 'Usuario creado con éxito') + vehicleMessage);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      setError(`Error al ${editMode ? 'actualizar' : 'crear'} el usuario: ${errorMessage}`);
      console.error('Submit error:', err);
    }
  };

  // AGREGADO: Función para cancelar edición
  const handleCancelEdit = () => {
    setEditMode(false);
    setFormData({
      name: '',
      lastname: '',
      email: '',
      idCard: '',
      typeID: '',
      password: '',
      role: '',
      isEmailVerified: false,
      subscription: ''
    });
    setSelectedParkingIds([]);
    setAdminData(null);
    setVehicleData({ plate: '', brand: '', model: '', type: 'Carro' });
    setEditingVehiclePlate(null);
    setUserVehicles([]);
    setShowVehicleForm(false);
    setError('');
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setBulkUploading(true);
    setBulkUploadResult(null);

    try {
      const result = await vehicleService.bulkUpload(file, {
        brand: 'N/A',
        model: 'N/A',
        type: 'Carro',
        company: 'Philips',
      });
      setBulkUploadResult(result);
      alert(result.message);
    } catch (err) {
      alert('Error al cargar archivo: ' + (err.response?.data?.message || err.message));
    } finally {
      setBulkUploading(false);
      if (bulkFileRef.current) bulkFileRef.current.value = '';
    }
  };

  return (
    <div className="ugm-user-management-container">
      <h1 className="ugm-main-title">Gestión de usuarios</h1>

      {error && <div className="ugm-error-message">{error}</div>}

      {/* Form Section */}
      <div className="ugm-form-section">
        <div className="ugm-section-header">
          <h2 className="ugm-section-title">{editMode ? 'Editar usuario' : 'Registrar usuario'}</h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="ugm-form-grid">
            <div className="ugm-col-span-1">
              <div className="ugm-form-group">
                <label className="ugm-form-label">Nombre</label>
                <div className="ugm-input-with-icon">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="ugm-form-control"
                    required
                  />
                </div>
              </div>

              <div className="ugm-form-group">
                <label className="ugm-form-label">Apellido</label>
                <div className="ugm-input-with-icon">
                  <input
                    type="text"
                    name="lastname"
                    value={formData.lastname}
                    onChange={handleChange}
                    className="ugm-form-control"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="ugm-col-span-1">
              <div className="ugm-form-group">
                <label className="ugm-form-label">E-mail</label>
                <div className="ugm-input-with-icon">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="ugm-form-control"
                    required
                  />
                </div>
              </div>

              <div className="ugm-form-group">
                <label className="ugm-form-label">Contraseña{editMode ? ' (dejar en blanco para mantener la actual)' : ''}</label>
                <div className="ugm-input-with-icon">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="ugm-form-control ugm-password-input"
                    {...(!editMode && { required: true })}
                  />
                  <button
                    type="button"
                    className="ugm-password-icon-button"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="ugm-col-span-1">
              <div className="ugm-form-group">
                <label className="ugm-form-label">Cédula</label>
                <div className="ugm-input-with-icon">
                  <input
                    type="text"
                    name="idCard"
                    value={formData.idCard}
                    onChange={handleChange}
                    className="ugm-form-control"
                    required
                  />
                </div>
              </div>

              <div className="ugm-form-group">
                <label className="ugm-form-label">Tipo de cédula</label>
                <select
                  name="typeID"
                  value={formData.typeID}
                  onChange={handleChange}
                  className="ugm-form-control"
                >
                  <option>Cédula</option>
                  <option>DIMEX</option>
                  <option>Juridica</option>
                  <option>Cédula de identidad física</option>
                  <option>NITE</option>
                  <option>Pasaporte</option>
                </select>
              </div>
            </div>
          </div>

          <div className="ugm-bottom-row">
            <div className="ugm-bottom-row-left">
              <div className="ugm-form-group">
                <label className="ugm-form-label">Tipo de usuario</label>
                <select
                  className="ugm-form-control"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                >
                  <option value="UserApp">Cliente</option>
                  <option value="ClientService">Empleado</option>
                  <option value="Admin">Administrador</option>
                </select>
              </div>

              <div className="ugm-form-group" style={{ marginTop: '0.5rem' }}>
                <label className="ugm-form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="isEmailVerified"
                    checked={formData.isEmailVerified}
                    onChange={(e) => setFormData(prev => ({ ...prev, isEmailVerified: e.target.checked }))}
                    style={{ width: '18px', height: '18px', accentColor: '#f97316' }}
                  />
                  Email verificado
                  {formData.isEmailVerified ? (
                    <span style={{ color: '#4ade80', fontSize: '0.85rem' }}>&#10003; Verificado</span>
                  ) : (
                    <span style={{ color: '#f87171', fontSize: '0.85rem' }}>&#10007; No verificado</span>
                  )}
                </label>
              </div>
            </div>

            <div className="ugm-bottom-row-right">
              {editMode && (
                <button
                  type="button"
                  className="ugm-btn ugm-btn-secondary ugm-mr-2"
                  onClick={handleCancelEdit}
                >
                  Cancelar
                </button>
              )}
              <button
                type="submit"
                className="ugm-btn ugm-btn-primary"
              >
                {editMode ? 'Actualizar' : 'Guardar'}
              </button>
            </div>
          </div>

          {/* Sección de parqueos para Administrador */}
          {formData.role === 'Admin' && (
            <div className="ugm-parking-assign-section">
              <div className="ugm-parking-assign-header">
                <p className="ugm-parking-assign-title">Parqueos asignados</p>
                <button
                  type="button"
                  className="ugm-parking-advanced-btn"
                  onClick={handleOpenAdminParkingModal}
                  title="Gestionar asignaciones de parqueo para administradores"
                >
                  Gestión Avanzada
                </button>
              </div>

              <div className="ugm-parking-checklist">
                {parkingsLoading ? (
                  <div className="ugm-parking-checklist-empty">
                    Cargando parqueos...
                  </div>
                ) : registeredParkings.length === 0 ? (
                  <div className="ugm-parking-checklist-empty">
                    No hay parqueos disponibles
                  </div>
                ) : (
                  registeredParkings.map(parking => (
                    <label
                      key={parking._id}
                      htmlFor={`parking-${parking._id}`}
                      className={`ugm-parking-check-item ${selectedParkingIds.includes(parking._id) ? 'selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        id={`parking-${parking._id}`}
                        checked={selectedParkingIds.includes(parking._id)}
                        onChange={() => handleParkingSelection(parking._id)}
                        className="ugm-parking-checkbox"
                      />
                      <div className="ugm-parking-check-info">
                        <span className="ugm-parking-check-name">{parking.parkingName}</span>
                        <span className="ugm-parking-check-detail">
                          Capacidad: {parking.capacity}
                          {parking.tarify && ` · ₡${parking.tarify}/hora`}
                        </span>
                      </div>
                    </label>
                  ))
                )}
              </div>

              {selectedParkingIds.length > 0 && (
                <p className="ugm-parking-selected-count">
                  {selectedParkingIds.length} parqueo{selectedParkingIds.length !== 1 ? 's' : ''} seleccionado{selectedParkingIds.length !== 1 ? 's' : ''}
                  {editMode && ' — se actualizarán al guardar'}
                </p>
              )}
            </div>
          )}

          {/* Sección de Vehículo */}
          <div className="ugm-vehicle-section">
            {/* Lista de vehículos existentes al editar */}
            {editMode && userVehicles.length > 0 && (
              <div className="ugm-vehicle-list">
                <p className="ugm-vehicle-list-title">Vehículos registrados</p>
                <div className="ugm-vehicle-tags">
                  {userVehicles.map((vehicle, index) => (
                    <div
                      key={index}
                      className={`ugm-vehicle-tag ${editingVehiclePlate === (vehicle.plate || vehicle) ? 'editing' : ''}`}
                    >
                      <Car size={13} />
                      <span className="ugm-vehicle-tag-plate">{vehicle.plate || vehicle}</span>
                      {vehicle.brand && (
                        <span className="ugm-vehicle-tag-detail">{vehicle.brand} {vehicle.model}</span>
                      )}
                      <button
                        type="button"
                        className="ugm-vehicle-tag-edit"
                        onClick={() => handleEditVehicle(vehicle)}
                        title="Editar vehículo"
                      >
                        <Edit size={12} />
                      </button>
                      <button
                        type="button"
                        className="ugm-vehicle-tag-delete"
                        onClick={() => handleDeleteVehicle(vehicle.plate || vehicle)}
                        title="Eliminar vehículo"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              type="button"
              className="ugm-vehicle-toggle"
              onClick={() => {
                if (showVehicleForm && editingVehiclePlate) {
                  handleCancelVehicleEdit();
                } else {
                  setEditingVehiclePlate(null);
                  setVehicleData({ plate: '', brand: '', model: '', type: 'Carro' });
                  setShowVehicleForm(!showVehicleForm);
                }
              }}
            >
              <Car size={16} />
              {showVehicleForm ? (editingVehiclePlate ? 'Cancelar edición' : 'Ocultar formulario') : 'Agregar vehículo'}
              <span className={`ugm-vehicle-chevron ${showVehicleForm ? 'open' : ''}`}>▼</span>
            </button>

            <div className={`ugm-vehicle-form-wrapper ${showVehicleForm ? 'open' : ''}`}>
              <div className={`ugm-vehicle-form ${editingVehiclePlate ? 'ugm-vehicle-form-editing' : ''}`}>
                <p className="ugm-vehicle-form-title">
                  {editingVehiclePlate
                    ? `Editando vehículo: ${editingVehiclePlate}`
                    : editMode ? 'Agregar nuevo vehículo' : 'Datos del vehículo'}
                </p>
                <div className="ugm-form-grid">
                  <div className="ugm-col-span-1">
                    <div className="ugm-form-group">
                      <label className="ugm-form-label">Placa</label>
                      <input
                        type="text"
                        name="plate"
                        value={vehicleData.plate}
                        onChange={handleVehicleChange}
                        className="ugm-form-control"
                        placeholder="ABC123"
                        style={{ textTransform: 'uppercase' }}
                        disabled={!!editingVehiclePlate}
                      />
                    </div>
                    <div className="ugm-form-group">
                      <label className="ugm-form-label">Marca</label>
                      <input
                        type="text"
                        name="brand"
                        value={vehicleData.brand}
                        onChange={handleVehicleChange}
                        className="ugm-form-control"
                        placeholder="Toyota, Honda, etc."
                      />
                    </div>
                  </div>
                  <div className="ugm-col-span-1">
                    <div className="ugm-form-group">
                      <label className="ugm-form-label">Modelo</label>
                      <input
                        type="text"
                        name="model"
                        value={vehicleData.model}
                        onChange={handleVehicleChange}
                        className="ugm-form-control"
                        placeholder="Corolla, Civic, etc."
                      />
                    </div>
                    <div className="ugm-form-group">
                      <label className="ugm-form-label">Tipo</label>
                      <select
                        name="type"
                        value={vehicleData.type}
                        onChange={handleVehicleChange}
                        className="ugm-form-control"
                      >
                        <option value="Carro">Carro</option>
                        <option value="Moto">Moto</option>
                      </select>
                    </div>
                  </div>
                </div>
                {editMode && (
                  <div className="ugm-vehicle-form-actions">
                    <button
                      type="button"
                      className="ugm-btn ugm-btn-primary"
                      onClick={handleSaveVehicle}
                    >
                      {editingVehiclePlate ? 'Guardar cambios' : 'Registrar vehículo'}
                    </button>
                    {editingVehiclePlate && (
                      <button
                        type="button"
                        className="ugm-btn ugm-btn-secondary"
                        onClick={handleCancelVehicleEdit}
                      >
                        Cancelar
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Users Table Section */}
      <div className="ugm-table-section">
        <div className="ugm-section-header">
          <h2 className="ugm-section-title">Usuarios Registrados</h2>
          <div className="ugm-flex-space-x">
            <input
              type="text"
              placeholder="Número de cédula"
              className="ugm-form-control"
              value={filterCedula}
              onChange={handleFilterChange}
            />
            <button
              type="button"
              className="ugm-btn ugm-btn-primary"
              onClick={applyFilter}
            >
              Filtrar
            </button>
            <button
              type="button"
              className="ugm-btn ugm-btn-secondary ugm-btn-icon"
              onClick={resetFilter}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M8 12h8" />
              </svg>
            </button>
          </div>
        </div>

        <div className="ugm-table-container">
          {loading ? (
            <div className="ugm-loading">Cargando usuarios...</div>
          ) : (
            <>
            <div className='ugm-table-wrapper'>
              <table className='ugm-users-table'>
                <thead className='ugm-table-header'>
                  <tr>
                    <th>Cédula</th>
                    <th>Tipo Cédula</th>
                    <th>Email</th>
                    <th>Nombre</th>
                    <th>Apellido</th>
                    <th>Rol</th>
                    <th>Subscripción</th>
                    <th>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="ugm-text-center">No hay usuarios para mostrar</td>
                    </tr>
                  ) : (
                    filteredUsers
                      .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                      .map((user) => (
                      <tr key={user._id}>
                        <td title={user.idCard}>{user.idCard}</td>
                        <td>{user.typeID}</td>
                        <td title={user.email}>{user.email}</td>
                        <td>{user.name}</td>
                        <td>{user.lastname}</td>
                        <td>{translateRole(user.role)}</td>
                        <td>{user.subscription || 'No definida'}</td>
                        <td>
                          <div className="ugm-flex-space-x">
                            <button
                              className="ugm-action-icon"
                              onClick={() => handleEdit(user)}
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              className="ugm-action-icon ugm-delete-icon"
                              onClick={() => handleDelete(user.idCard)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginacion */}
            {filteredUsers.length > rowsPerPage && (
              <div className="ugm-pagination">
                <button
                  className="ugm-btn ugm-btn-secondary"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                >
                  Anterior
                </button>
                <span className="ugm-pagination-info">
                  Pagina {currentPage} de {Math.ceil(filteredUsers.length / rowsPerPage)} ({filteredUsers.length} usuarios)
                </span>
                <button
                  className="ugm-btn ugm-btn-secondary"
                  disabled={currentPage >= Math.ceil(filteredUsers.length / rowsPerPage)}
                  onClick={() => setCurrentPage(p => p + 1)}
                >
                  Siguiente
                </button>
              </div>
            )}
            </>
          )}
        </div>
      </div>

      {showAdminParkingModal && (
        <AdminParkingManagement
          onClose={handleCloseAdminParkingModal}
          refreshUsers={handleRefreshAfterParkingUpdate}
        />
      )}

      {/* Seccion de Carga Masiva de Vehiculos y Usuarios */}
      <div className="ugm-form-section" style={{ marginTop: '2rem' }}>
        <div className="ugm-section-header">
          <h2 className="ugm-section-title">Carga Masiva de Empleados</h2>
        </div>
        <div style={{ padding: '1.5rem' }}>
          <p style={{ marginBottom: '1rem', color: '#9ca3af' }}>
            Sube un archivo Excel (.xlsx) con una columna de placas. Se creara automaticamente un vehiculo, un usuario y una cuenta para cada placa.
          </p>
          <p style={{ marginBottom: '1rem', color: '#64748b', fontSize: '0.85rem' }}>
            <strong>Datos generados por empleado:</strong> Usuario con rol "UserApp", contrasena por defecto "Philips2025!",
            email: placa@philips.employee, cedula: PHILIPS-PLACA
          </p>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleBulkUpload}
            ref={bulkFileRef}
            style={{ display: 'none' }}
          />
          <button
            onClick={() => bulkFileRef.current?.click()}
            disabled={bulkUploading}
            className="ugm-btn ugm-btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Upload size={16} />
            {bulkUploading ? 'Cargando...' : 'Subir archivo XLSX'}
          </button>

          {bulkUploadResult && (
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              borderRadius: '8px',
              backgroundColor: '#1e293b',
              border: '1px solid #334155'
            }}>
              <p><strong>Resultados:</strong></p>
              <p>Total en archivo: {bulkUploadResult.results?.total || 0}</p>
              <p style={{ color: '#4ade80' }}>
                Vehiculos creados: {bulkUploadResult.results?.vehiclesCreated || 0}
              </p>
              <p style={{ color: '#4ade80' }}>
                Usuarios creados: {bulkUploadResult.results?.usersCreated || 0}
              </p>
              <p style={{ color: '#4ade80' }}>
                Cuentas (UserApp) creadas: {bulkUploadResult.results?.userAppsCreated || 0}
              </p>
              <p style={{ color: '#fbbf24' }}>
                Omitidos (ya existian): {bulkUploadResult.results?.skipped || 0}
              </p>
              {bulkUploadResult.results?.errors?.length > 0 && (
                <details style={{ marginTop: '0.5rem' }}>
                  <summary style={{ cursor: 'pointer', color: '#f87171' }}>
                    Ver detalles ({bulkUploadResult.results.errors.length} notas)
                  </summary>
                  <ul style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
                    {bulkUploadResult.results.errors.map((e, i) => (
                      <li key={i}>{e.plate}: {e.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      </div>

    </div>

  );
};

// Función para traducir roles a español
const translateRole = (role) => {
  const roles = {
    'UserApp': 'Cliente',
    'ClientService': 'Empleado',
    'Admin': 'Administrador'
  };
  return roles[role] || role;
};

export default Gestion_de_usuarios;